import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ErrorHandlerService } from '@app/_services/errorHandler.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IframeDialogDialogResult } from './iframedialog.result';

@Component({
  selector: 'iframe-terms-dialog',
  templateUrl: './iframe-dialog.component.html',
  styleUrls: ['./iframe-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatProgressSpinner, FormsModule, ReactiveFormsModule, AsyncPipe, TranslatePipe],
})
export class IframeDialogComponent implements OnInit, AfterViewInit {
  private readonly httpClient = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly modalRef = inject(BsModalRef);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  title: string | undefined | null;
  text!: string;
  button_confirm_text!: string;
  button_cancel_text!: string;
  url!: string;
  checkboxText?: string;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  htmlSrc?: SafeHtml;
  form!: UntypedFormGroup;
  onClose!: Subject<IframeDialogDialogResult>;

  ngOnInit(): void {
    this.loadingSubject.next(true);
    this.onClose = new Subject();

    this.form = new UntypedFormGroup({
      checkbox: new UntypedFormControl(false),
    });
  }

  ngAfterViewInit(): void {
    this.httpClient
      .get(this.url, { responseType: 'text' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.htmlSrc = this.sanitizer.bypassSecurityTrustHtml(result);

          // If title was not set, try to get title from html
          if (!this.title) {
            const domParser = new DOMParser();
            const htmlElement = domParser.parseFromString(result, 'text/html');
            const titleElements = htmlElement.getElementsByTagName('title');

            if (titleElements.length > 0) {
              this.title = titleElements[0].text;
            }
          }
          this.loadingSubject.next(false);
        },
        error: (errorResponse: HttpErrorResponse) => {
          console.error(errorResponse);
          this.loadingSubject.next(false);

          this.errorHandler.displayErrorDialog(errorResponse)?.then(() => {
            this.onCancel();
          });
        },
      });
  }

  get f() {
    return this.form.controls;
  }

  onConfirm(): void {
    this.onClose.next({ confirm: true, checkboxValue: this.f.checkbox.value });
    this.modalRef.hide();
  }

  onCancel(): void {
    this.onClose.next({ confirm: false, checkboxValue: this.f.checkbox.value });
    this.modalRef.hide();
  }

  static showDialogTranslated(
    translate: TranslateService,
    modalService: BsModalService,
    url: string,
    title?: string,
    text?: string,
    ok?: string,
    cancel?: string,
    modalClass?: string,
    translateParams?: object,
    checkboxText?: string,
  ): Promise<BsModalRef<IframeDialogComponent>> {
    return new Promise<BsModalRef<IframeDialogComponent>>((resolve) => {
      const resourceArray = [];

      if (title) {
        resourceArray.push(title);
      }

      if (text) {
        resourceArray.push(text);
      }

      if (ok) {
        resourceArray.push(ok);
      }

      if (cancel) {
        resourceArray.push(cancel);
      }

      firstValueFrom(translate.get(resourceArray, translateParams)).then((res) => {
        const titleRes = title ? res[title] : '';
        const textRes = text ? res[text] : '';
        const okRes = ok ? res[ok] : undefined;
        const cancelRes = cancel ? res[cancel] : undefined;

        const modalRef = modalService.show(IframeDialogComponent, {
          class: modalClass,
          ignoreBackdropClick: true,
          initialState: {
            title: titleRes,
            text: textRes,
            button_confirm_text: okRes,
            button_cancel_text: cancelRes,
            url: url,
            checkboxText: checkboxText,
          },
        });
        resolve(modalRef);
      });
    });
  }
}
